import { useEffect, useRef } from 'react'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import {
  useSequencerStore,
  isPristineDefaultContainer,
  DEFAULT_CONTAINER_ID,
  LOCAL_JAM_CONTAINER_ID,
  ensureMasterFftEffect,
  type ProjectContainer,
  type ProjectContainerBank,
  type ContainerSongEntry,
  type SongScene,
  type MidiPadDef,
  type PadContent,
  type PadMidiRouting,
  type CustomSampleRef,
  type KeysModeConfig,
} from '@/stores/sequencerStore'
import { apiClient } from '@/lib/api'
import type { BankId, MidiProjectConfig } from '@busswerks/shared'
import type { KitId } from '@/hooks/useAudioEngine'

/**
 * Cloud sync for sequencer Projects (containers) — Phase 7 rewrite.
 *
 * Pull on mount: GET /api/v1/sequencer/containers → each container's
 * full blob. The server stores banks+patterns inline; we flatten
 * patterns into the store's `projects` map (the flat "patterns pool")
 * and wire bank.patternIds by key.
 *
 * Push on change: debounced 800ms. For each local container:
 *   - No server id yet → POST (server mints id); remap local id to
 *     the returned uuid, preserving content.
 *   - Has server id → PUT with version; 409 triggers a refetch.
 *
 * Server never accepts a client-supplied container id, which kills
 * the old "PUT → 404 → POST" duplicate-seeding loop. Local state
 * survives offline via Zustand persist; the sync loop only reconciles
 * when the user regains connectivity.
 */
export function useSyncSequencerProjects(): {
  syncing: boolean
  error: string | null
} {
  const { features, tier } = useEffectiveFeatures()
  const canSync = features?.sequencerSave === true && tier !== 'free'

  const containers = useSequencerStore(s => s.containers)
  const projects = useSequencerStore(s => s.projects)
  const currentContainerId = useSequencerStore(s => s.currentContainerId)
  const containerSyncState = useSequencerStore(s => s.containerSyncState)
  const recordContainerSync = useSequencerStore(s => s.recordContainerSync)
  const forgetContainerSync = useSequencerStore(s => s.forgetContainerSync)
  const remapContainerId = useSequencerStore(s => s.remapContainerId)
  const deleteContainerLocal = useSequencerStore(s => s.deleteContainer)
  const hydratedOnce = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // In-memory hash cache keyed by local container id. Remembers the
  // last-pushed content so the debounced push doesn't re-send
  // identical state. We don't persist the hash (payloads are large;
  // the sync state itself is persisted via `containerSyncState` so a
  // reload-then-unchanged-edit doesn't re-POST).
  const hashCache = useRef(new Map<string, string>())

  // ── Pull once on mount ────────────────────────────────────
  useEffect(() => {
    if (!canSync || hydratedOnce.current) return
    hydratedOnce.current = true
    let cancelled = false
    ;(async () => {
      try {
        type ListEntry = { id: string }
        const list = await apiClient.get<ListEntry[]>(
          '/api/v1/sequencer/containers',
        )
        if (cancelled || list.length === 0) return
        const full = await Promise.all(
          list.map(entry =>
            apiClient
              .get<ServerContainerBlob>(
                `/api/v1/sequencer/containers/${entry.id}`,
              )
              .catch(err => {
                console.warn(`Failed to fetch container ${entry.id}:`, err)
                return null
              }),
          ),
        )
        if (cancelled) return

        const state = useSequencerStore.getState()
        const nextProjects = { ...state.projects }
        const nextContainers: Record<string, ProjectContainer> = {}

        for (const blob of full) {
          if (!blob) continue
          const banks: Record<BankId, ProjectContainerBank> = {
            A: emptyBank('A'),
            B: emptyBank('B'),
            C: emptyBank('C'),
            D: emptyBank('D'),
          }
          for (const bankId of ['A', 'B', 'C', 'D'] as const) {
            const serverBank = blob.banks?.[bankId]
            if (!serverBank) continue
            const patternIds: string[] = []
            for (const [pid, pattern] of Object.entries(
              serverBank.patterns ?? {},
            )) {
              nextProjects[pid] = {
                id: pid,
                name: (pattern as { name?: string }).name ?? 'Pattern',
                bpm: (pattern as { bpm?: number }).bpm ?? 120,
                kitId:
                  (pattern as { kitId?: string }).kitId ?? serverBank.kitId,
                stepCount:
                  (pattern as { stepCount?: 16 | 32 | 64 | 128 }).stepCount ??
                  16,
                midiEnabled:
                  (pattern as { midiEnabled?: boolean }).midiEnabled ??
                  serverBank.midiEnabled ??
                  true,
                steps:
                  (pattern as { steps?: Record<number, unknown[]> }).steps ??
                  {},
                midiPads: serverBank.midiPads ?? [],
                midiPadSteps:
                  (pattern as { midiPadSteps?: Record<string, unknown[]> })
                    .midiPadSteps ?? {},
                customSamples: serverBank.customSamples,
                padOutputs: serverBank.padOutputs,
                padDefs: serverBank.padDefs,
                keysMode: serverBank.keysMode,
              } as (typeof nextProjects)[string]
              patternIds.push(pid)
            }
            banks[bankId] = {
              id: bankId,
              label: serverBank.label ?? `Bank ${bankId}`,
              kitId: (serverBank.kitId as KitId) ?? 'classic',
              padDefs: serverBank.padDefs as
                | Record<number, PadContent>
                | undefined,
              padOutputs: serverBank.padOutputs as
                | Record<number, PadMidiRouting>
                | undefined,
              customSamples: serverBank.customSamples as
                | Record<number, CustomSampleRef>
                | undefined,
              midiPads: (serverBank.midiPads ?? []) as MidiPadDef[],
              midiEnabled: serverBank.midiEnabled ?? true,
              keysMode: serverBank.keysMode as KeysModeConfig | undefined,
              activePatternId:
                serverBank.activePatternId ?? patternIds[0] ?? '',
              patternIds,
            }
          }
          const localId = blob.id
          // Server blobs predate the Phase-7 bus schema and don't
          // carry a `buses` field. Without the heal, every freshly-
          // pulled container would land with `buses: undefined` and
          // the Plugin pane's master-FFT fallback would find nothing
          // to select. `ensureMasterFftEffect` synthesises the master
          // row + seeds the FFT analyzer — idempotent so server blobs
          // that DO include buses (future schema version) keep their
          // state intact.
          const hydrated: ProjectContainer = {
            id: localId,
            // Server rows predating the Phase-7 schema may omit
            // `name` — fall back so downstream `name.match(...)`
            // calls in the store don't crash the Jam/Projects route.
            name: blob.name ?? 'Untitled project',
            createdAt: blob.createdAt,
            updatedAt: blob.updatedAt,
            version: blob.version,
            activeBankId: (blob.activeBankId as BankId) ?? 'A',
            banks,
            songMode: blob.songMode ?? false,
            song: (blob.song ?? []) as ContainerSongEntry[],
            scenes: (blob.scenes ?? []) as SongScene[],
            midiConfig:
              (blob.midiConfig as MidiProjectConfig | undefined) ?? undefined,
            slicerConfig: {
              sampleIds: blob.slicerConfig?.sampleIds ?? [],
              activeSampleId: blob.slicerConfig?.activeSampleId,
            },
          }
          nextContainers[localId] = ensureMasterFftEffect(hydrated)
          const hash = hashContainerWire(blob)
          hashCache.current.set(localId, hash)
          recordContainerSync(localId, localId, blob.version)
        }

        // Preserve system-level local containers (Jam, the pristine
        // default seed when remote is empty). Drop everything else
        // that the server doesn't know about — those are ghosts from
        // the pre-fix duplicate bug, where `forgetContainerSync` +
        // re-POST produced orphan local rows with no matching server
        // row. Keeping them here is what caused users to see 403
        // "project limit exceeded" POSTs on every load: the push
        // loop iterated them and tried to create them as fresh rows.
        //
        // New-project creation goes through `insertServerContainer`
        // which seeds `containerSyncState` atomically with the local
        // row, so there is no legitimate reason for a local UUID-keyed
        // container to lack a server id.
        const remoteHasContainers = Object.keys(nextContainers).length > 0
        const droppedOrphans: string[] = []
        for (const [lid, c] of Object.entries(state.containers)) {
          if (lid in nextContainers) continue
          // Jam + anonymous local-jam live outside the /containers
          // collection — always preserved. Heal in case the local
          // record was pre-v13 and missing the master FFT.
          if (lid === 'jam' || lid === LOCAL_JAM_CONTAINER_ID) {
            nextContainers[lid] = ensureMasterFftEffect(c)
            continue
          }
          // Default seed is dropped when remote has any containers
          // (preserving it would re-seed dupes on every device).
          if (lid === DEFAULT_CONTAINER_ID) {
            if (
              !remoteHasContainers ||
              !isPristineDefaultContainer(c, state.projects)
            ) {
              nextContainers[lid] = ensureMasterFftEffect(c)
            }
            continue
          }
          // UUID-keyed container that the server doesn't have: orphan.
          // Drop it — and clear its sync record so the push loop can't
          // try to resurrect it as a POST on the next tick.
          droppedOrphans.push(lid)
        }
        if (droppedOrphans.length > 0) {
          console.warn(
            `Dropping ${droppedOrphans.length} orphan local container(s) with no server row:`,
            droppedOrphans,
          )
        }

        const nextCurrentId = nextContainers[state.currentContainerId]
          ? state.currentContainerId
          : Object.keys(nextContainers)[0]
        const nextCurrentPatternId =
          nextContainers[nextCurrentId]?.banks[
            nextContainers[nextCurrentId].activeBankId
          ]?.activePatternId ?? state.currentProjectId

        // Drop sync records for containers that no longer live in the
        // local map — without this, the push loop could still iterate
        // a stale record and try to PUT a row we just evicted, which
        // loops back through the 404 → purge → "did we just lose
        // data?" path.
        const nextSyncState = Object.fromEntries(
          Object.entries(
            useSequencerStore.getState().containerSyncState,
          ).filter(([lid]) => lid in nextContainers),
        )
        useSequencerStore.setState({
          projects: nextProjects,
          containers: nextContainers,
          currentContainerId: nextCurrentId,
          currentProjectId: nextCurrentPatternId,
          containerSyncState: nextSyncState,
        })
      } catch (err) {
        console.warn('Sequencer container hydrate failed:', err)
      }
    })()
    return () => {
      cancelled = true
    }
    // Deps are intentionally minimal — we want the pull to run once
    // per mount; `recordContainerSync` is stable (Zustand action).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSync])

  // ── Push debounced ────────────────────────────────────────
  useEffect(() => {
    if (!canSync) return
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      for (const [localId, container] of Object.entries(containers)) {
        // Skip the Jam container — it has its own backend row at
        // /api/v1/sequencer/jam and shouldn't be PUT/POSTed against
        // the /containers collection. Without this guard, the jam
        // hash would change on every edit and the sync loop would
        // hit /containers/jam which 404s.
        if (
          localId === 'jam' ||
          localId === DEFAULT_CONTAINER_ID ||
          localId === LOCAL_JAM_CONTAINER_ID
        )
          continue
        const wire = toWireFormat(container, projects)
        const hash = hashContainerWire(wire)
        const syncInfo = containerSyncState[localId]
        const lastHash = hashCache.current.get(localId)
        if (syncInfo && lastHash === hash) continue

        if (!syncInfo) {
          // Never synced. POST; server mints the id. `containerSyncState`
          // is persisted, so even if the hook unmounts between POSTs,
          // we won't POST the same container twice.
          try {
            const created = await apiClient.post<ServerContainerBlob>(
              '/api/v1/sequencer/containers',
              stripEnvelope(wire),
            )
            if (created.id !== localId) {
              // Remap everything — container key, currentContainerId,
              // and the persisted sync entry — in one store action so
              // subsequent pushes target the real server id.
              remapContainerId(localId, created.id, created.version)
              hashCache.current.delete(localId)
              hashCache.current.set(created.id, hash)
            } else {
              recordContainerSync(localId, created.id, created.version)
              hashCache.current.set(localId, hash)
            }
          } catch (err: unknown) {
            const status =
              err && typeof err === 'object' && 'status' in err
                ? (err as { status: number }).status
                : 0
            if (status === 403) {
              // Server rejected the POST (likely "project limit
              // exceeded"). Drop the local container so we don't
              // hammer the endpoint on every push tick. If the user
              // actually meant to create this, they'd have hit the
              // explicit + New Project flow which routes through
              // `insertServerContainer` — reaching this branch means
              // the container is a leftover from an older bug.
              console.warn(
                `Dropping local container ${localId} after POST 403 — cap exceeded or duplicate ghost`,
              )
              deleteContainerLocal(localId)
              hashCache.current.delete(localId)
            } else {
              console.warn(`Failed to POST container ${localId}:`, err)
            }
          }
          continue
        }

        // Already synced — PUT with version.
        try {
          const updated = await apiClient.put<ServerContainerBlob>(
            `/api/v1/sequencer/containers/${syncInfo.serverId}`,
            { ...stripEnvelope(wire), version: syncInfo.version },
          )
          recordContainerSync(localId, syncInfo.serverId, updated.version)
          hashCache.current.set(localId, hash)
        } catch (err: unknown) {
          const status =
            err && typeof err === 'object' && 'status' in err
              ? (err as { status: number }).status
              : 0
          if (status === 409) {
            // Version conflict — another client (or this client
            // across a refresh) advanced the server row past our
            // recorded version. GET the current blob so we can
            // refresh just the version in `containerSyncState` and
            // retry the push with the latest ETag. Without this the
            // next PUT reuses the stale version and 409s forever.
            //
            // Last-writer-wins on content: we don't merge server
            // content into local because that would clobber the
            // user's in-flight edits. The next successful PUT will
            // overwrite the server side with what the user just did.
            try {
              const fresh = await apiClient.get<ServerContainerBlob>(
                `/api/v1/sequencer/containers/${syncInfo.serverId}`,
              )
              recordContainerSync(localId, syncInfo.serverId, fresh.version)
              hashCache.current.delete(localId)
            } catch (refetchErr) {
              console.warn(
                `Container ${syncInfo.serverId} version conflict and refetch failed — will retry on next change:`,
                refetchErr,
              )
              hashCache.current.delete(localId)
            }
          } else if (status === 404) {
            // Server doesn't have this container anymore — the row
            // was deleted (either by this user on another tab or by
            // the Projects-list delete mutation). Trust the server:
            // purge the local container + sync record instead of
            // re-POSTing. The old behavior ("forget sync, re-POST")
            // was a resurrection machine: every delete produced a
            // new dup under a fresh uuid, and the user's project
            // list climbed back to the cap on every refresh.
            deleteContainerLocal(localId)
            forgetContainerSync(localId)
            hashCache.current.delete(localId)
          } else {
            console.warn(`Failed to PUT container ${syncInfo.serverId}:`, err)
          }
        }
      }
    }, 800)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [
    canSync,
    containers,
    projects,
    currentContainerId,
    containerSyncState,
    recordContainerSync,
    forgetContainerSync,
    remapContainerId,
    deleteContainerLocal,
  ])

  return { syncing: canSync, error: null }
}

// ── Helpers ──────────────────────────────────────────────

/** Server blob shape — mirrors the container route's response. */
interface ServerContainerBlob {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  version: number
  activeBankId: string
  banks: Record<
    string,
    {
      id: string
      label?: string
      kitId?: string
      padDefs?: Record<number, unknown>
      padOutputs?: Record<number, unknown>
      customSamples?: Record<number, unknown>
      midiPads?: unknown[]
      midiEnabled?: boolean
      keysMode?: unknown
      activePatternId?: string
      patterns?: Record<string, unknown>
    }
  >
  songMode?: boolean
  song?: unknown[]
  scenes?: unknown[]
  midiConfig?: unknown
  slicerConfig?: { sampleIds?: string[]; activeSampleId?: string }
}

function emptyBank(id: BankId): ProjectContainerBank {
  return {
    id,
    label: `Bank ${id}`,
    kitId: 'classic',
    midiPads: [],
    midiEnabled: true,
    activePatternId: '',
    patternIds: [],
  }
}

/**
 * Build the wire-format container blob from the local flat-pattern
 * shape. Each bank's `patternIds` get expanded to the full Pattern
 * records so the server receives one self-contained blob.
 */
function toWireFormat(
  container: ProjectContainer,
  projects: Record<
    string,
    ReturnType<typeof useSequencerStore.getState>['projects'][string]
  >,
): ServerContainerBlob {
  const banks: ServerContainerBlob['banks'] = {}
  for (const bankId of ['A', 'B', 'C', 'D'] as const) {
    const bank = container.banks[bankId]
    if (!bank) continue
    const patterns: Record<string, unknown> = {}
    // Promote-up safety: older local state (or any pattern whose
    // pad assignments were written before the setPadContent mirror
    // fix) keeps its `padDefs` at the pattern level only. The bank
    // is what the server persists, so if the bank's padDefs is
    // missing but any pattern in this bank has one, lift the first
    // non-empty pattern padDefs up to the bank so the wire format
    // actually carries them. Prevents silent loss of pad → slice
    // bindings on sync round-trip for existing projects.
    let promotedPadDefs: Record<number, unknown> | undefined
    if (!bank.padDefs || Object.keys(bank.padDefs).length === 0) {
      for (const pid of bank.patternIds) {
        const p = projects[pid]
        if (p?.padDefs && Object.keys(p.padDefs).length > 0) {
          promotedPadDefs = p.padDefs
          break
        }
      }
    }
    for (const pid of bank.patternIds) {
      const p = projects[pid]
      if (!p) continue
      patterns[pid] = {
        id: p.id,
        name: p.name,
        bpm: p.bpm,
        stepCount: p.stepCount,
        steps: p.steps,
        midiPadSteps: p.midiPadSteps,
      }
    }
    banks[bankId] = {
      id: bank.id,
      label: bank.label,
      kitId: bank.kitId,
      padDefs: bank.padDefs ?? promotedPadDefs,
      padOutputs: bank.padOutputs,
      customSamples: bank.customSamples,
      midiPads: bank.midiPads,
      midiEnabled: bank.midiEnabled,
      keysMode: bank.keysMode,
      activePatternId: bank.activePatternId,
      patterns,
    }
  }
  return {
    id: container.id,
    name: container.name,
    createdAt: container.createdAt,
    updatedAt: container.updatedAt,
    version: container.version,
    activeBankId: container.activeBankId,
    banks,
    songMode: container.songMode,
    song: container.song,
    scenes: container.scenes,
    midiConfig: container.midiConfig,
    slicerConfig: container.slicerConfig,
  }
}

/** Drop fields the server assigns itself (id/version/timestamps). */
function stripEnvelope(wire: ServerContainerBlob) {
  return {
    name: wire.name,
    activeBankId: wire.activeBankId,
    banks: wire.banks,
    songMode: wire.songMode ?? false,
    song: wire.song ?? [],
    scenes: wire.scenes ?? [],
    midiConfig: wire.midiConfig,
    slicerConfig: wire.slicerConfig ?? { sampleIds: [] },
  }
}

/** Stable hash for dirty-detection on the push loop. Deterministic
 *  JSON.stringify is good enough — false positives just cause one
 *  extra PUT. */
function hashContainerWire(wire: ServerContainerBlob): string {
  return JSON.stringify({
    name: wire.name,
    activeBankId: wire.activeBankId,
    banks: wire.banks,
    songMode: wire.songMode,
    song: wire.song,
    scenes: wire.scenes,
    midiConfig: wire.midiConfig,
    slicerConfig: wire.slicerConfig,
  })
}
